/**
 * Shared scripts runtime core.
 *
 * Environment-specific scope loading is injected by the host adapter.
 * The runtime owns:
 *   - scope bookkeeping
 *   - always/preload semantics
 *   - trigger dispatch
 *   - scoped facades
 *   - explicit scope-bound registration
 */
import { runTriggerPipeline } from './pipeline.js';

const VALID_SCOPE_TYPES = new Set(['tenant', 'event', 'template', 'page']);
const HEX32 = /^[a-fA-F0-9]{32}$/;

function normalizeMode(mode, fallback = 'preload') {
    return mode === 'always' ? 'always' : fallback;
}

function assertScopeType(type) {
    if (!VALID_SCOPE_TYPES.has(type)) {
        throw new Error(`[scripts] Invalid scope type: ${type}`);
    }
}

function assertScopeId(id) {
    const idString = String(id || '');
    if (!HEX32.test(idString)) {
        throw new Error(`[scripts] Invalid scope id: ${idString}`);
    }
    return idString;
}

function toScopeKey(type, id) {
    return `${type}-${id}`;
}

function mergeScopeMaps(baseScope, nextScope) {
    const merged = { ...(baseScope || {}) };
    if (!nextScope) return merged;
    if (nextScope.tenantId != null) merged.tenantId = nextScope.tenantId;
    if (nextScope.eventId != null) merged.eventId = nextScope.eventId;
    if (nextScope.templateId != null) merged.templateId = nextScope.templateId;
    if (nextScope.pageId != null) merged.pageId = nextScope.pageId;
    return merged;
}

function getScopeKeys(scope) {
    if (!scope) return [];
    const keys = [];
    if (scope.tenantId) keys.push(toScopeKey('tenant', String(scope.tenantId)));
    if (scope.eventId) keys.push(toScopeKey('event', String(scope.eventId)));
    if (scope.templateId) keys.push(toScopeKey('template', String(scope.templateId)));
    if (scope.pageId) keys.push(toScopeKey('page', String(scope.pageId)));
    return keys;
}

function normalizeTriggerScopeKeys(scope) {
    const scopeKeys = new Set();
    if (!scope) return scopeKeys;

    const addLegacyScope = (legacyScope) => {
        if (!legacyScope || typeof legacyScope !== 'object') return;

        const add = (value, cb) => {
            if (value == null) return;
            if (Array.isArray(value)) {
                for (const v of value) if (v != null) cb(v);
                return;
            }
            cb(value);
        };

        add(legacyScope.tenantId, id => scopeKeys.add(toScopeKey('tenant', String(id))));
        add(legacyScope.tenantIds, id => scopeKeys.add(toScopeKey('tenant', String(id))));

        add(legacyScope.eventId, id => scopeKeys.add(toScopeKey('event', String(id))));
        add(legacyScope.eventIds, id => scopeKeys.add(toScopeKey('event', String(id))));

        add(legacyScope.templateId, id => scopeKeys.add(toScopeKey('template', String(id))));
        add(legacyScope.templateIds, id => scopeKeys.add(toScopeKey('template', String(id))));

        add(legacyScope.pageId, id => scopeKeys.add(toScopeKey('page', String(id))));
        add(legacyScope.pageIds, id => scopeKeys.add(toScopeKey('page', String(id))));
    };

    if (Array.isArray(scope)) {
        for (const s of scope) {
            if (s?.type && s?.id) {
                scopeKeys.add(toScopeKey(s.type, String(s.id)));
            } else {
                addLegacyScope(s);
            }
        }
        return scopeKeys;
    }

    if (scope.type && scope.id) {
        scopeKeys.add(toScopeKey(scope.type, String(scope.id)));
        return scopeKeys;
    }

    addLegacyScope(scope);
    return scopeKeys;
}

function mergeTriggerScopes(baseScope, nextScope) {
    if (!baseScope) return nextScope;
    if (!nextScope) return baseScope;

    const isDescriptor = (v) => v && typeof v === 'object' && !Array.isArray(v) && v.type && v.id;

    if (Array.isArray(baseScope) || Array.isArray(nextScope) || isDescriptor(baseScope) || isDescriptor(nextScope)) {
        const toArray = (s) => {
            if (!s) return [];
            if (Array.isArray(s)) return s;
            if (isDescriptor(s)) return [s];
            return [s];
        };
        return [...toArray(baseScope), ...toArray(nextScope)];
    }

    return {
        ...baseScope,
        ...nextScope,
        tenantIds: [...new Set([...(baseScope.tenantIds || []), ...(nextScope.tenantIds || [])])],
        eventIds: [...new Set([...(baseScope.eventIds || []), ...(nextScope.eventIds || [])])],
        templateIds: [...new Set([...(baseScope.templateIds || []), ...(nextScope.templateIds || [])])],
        pageIds: [...new Set([...(baseScope.pageIds || []), ...(nextScope.pageIds || [])])]
    };
}

export function createScriptsRuntime({
    createScriptFactory,
    loadScopeSource,
    evaluateScopeSource,
    createRegistrationContext,
    onLoadError
}) {
    if (typeof createScriptFactory !== 'function') throw new Error('[scripts] createScriptFactory is required');
    if (typeof loadScopeSource !== 'function') throw new Error('[scripts] loadScopeSource is required');
    if (typeof evaluateScopeSource !== 'function') throw new Error('[scripts] evaluateScopeSource is required');

    const before = new Map();
    const after = new Map();
    const loadedScopes = new Map();
    const pendingLoads = new Map();

    function pruneEmptyTriggerEntries(map) {
        for (const [triggerName, handlers] of map) {
            if (!handlers || handlers.length === 0) map.delete(triggerName);
        }
    }

    function registerWithScope(scopeMeta, scriptId, scriptInstance) {
        const scopeKey = scopeMeta.scopeKey;
        const scopeMode = scopeMeta.mode;

        for (const { triggerName, fn, priority } of scriptInstance.beforeHandlers || []) {
            if (!before.has(triggerName)) before.set(triggerName, []);
            before.get(triggerName).push({ scriptId, fn, priority, __scopeKey: scopeKey, __scopeMode: scopeMode });
        }

        for (const { triggerName, fn, priority } of scriptInstance.afterHandlers || []) {
            if (!after.has(triggerName)) after.set(triggerName, []);
            after.get(triggerName).push({ scriptId, fn, priority, __scopeKey: scopeKey, __scopeMode: scopeMode });
        }
    }

    function createScopedRegistrar(scopeMeta) {
        return {
            createScript: createScriptFactory,
            register: (scriptId, scriptInstance) => registerWithScope(scopeMeta, scriptId, scriptInstance)
        };
    }

    async function loadScope(type, id, opts = {}) {
        assertScopeType(type);
        const idString = assertScopeId(id);
        const scopeKey = toScopeKey(type, idString);
        const normalizedMode = normalizeMode(opts.mode, 'preload');

        const existing = loadedScopes.get(scopeKey);
        if (existing) {
            if (existing.mode !== 'always' && normalizedMode === 'always') {
                existing.mode = 'always';
                for (const phaseMap of [before, after]) {
                    for (const [triggerName, handlers] of phaseMap) {
                        phaseMap.set(triggerName, handlers.map(h => h.__scopeKey === scopeKey ? { ...h, __scopeMode: 'always' } : h));
                    }
                }
            }
            return;
        }

        if (pendingLoads.has(scopeKey)) {
            await pendingLoads.get(scopeKey);
            const upgraded = loadedScopes.get(scopeKey);
            if (upgraded && upgraded.mode !== 'always' && normalizedMode === 'always') {
                upgraded.mode = 'always';
                for (const phaseMap of [before, after]) {
                    for (const [triggerName, handlers] of phaseMap) {
                        phaseMap.set(triggerName, handlers.map(h => h.__scopeKey === scopeKey ? { ...h, __scopeMode: 'always' } : h));
                    }
                }
            }
            return;
        }

        const scopeMeta = { type, id: idString, scopeKey, mode: normalizedMode };
        const registrationContext = createRegistrationContext
            ? createRegistrationContext(scopeMeta, createScopedRegistrar(scopeMeta))
            : createScopedRegistrar(scopeMeta);

        const loadPromise = (async () => {
            let source = opts?.preloadedSource;
            if (!source) {
                source = await loadScopeSource(scopeMeta, opts);
            }
            if (!source) {
                loadedScopes.set(scopeKey, scopeMeta);
                return;
            }
            await evaluateScopeSource(source, scopeMeta, registrationContext);
            loadedScopes.set(scopeKey, scopeMeta);
        })();

        pendingLoads.set(scopeKey, loadPromise);

        try {
            await loadPromise;
        } catch (error) {
            // Remove any partially-registered handlers from failed scope loads.
            if (loadedScopes.has(scopeKey)) loadedScopes.delete(scopeKey);
            for (const phaseMap of [before, after]) {
                for (const [triggerName, handlers] of phaseMap) {
                    phaseMap.set(triggerName, handlers.filter(h => h.__scopeKey !== scopeKey));
                }
                pruneEmptyTriggerEntries(phaseMap);
            }
            if (typeof onLoadError === 'function') onLoadError(error, scopeMeta);
            throw error;
        } finally {
            pendingLoads.delete(scopeKey);
        }
    }

    async function loadScopeMap(scope, mode = 'preload') {
        const work = [];
        if (scope?.tenantId) work.push(loadScope('tenant', scope.tenantId, { mode }));
        if (scope?.eventId) work.push(loadScope('event', scope.eventId, { mode }));
        if (scope?.templateId) work.push(loadScope('template', scope.templateId, { mode }));
        if (scope?.pageId) work.push(loadScope('page', scope.pageId, { mode }));
        await Promise.all(work);
    }

    async function loadScopes(scopes, opts = {}) {
        if (!Array.isArray(scopes) || scopes.length === 0) return;

        const work = [];
        for (const scope of scopes) {
            if (!scope || !scope.type || !scope.id) continue;
            work.push(loadScope(scope.type, scope.id, { 
                mode: scope.mode || 'preload',
                ...opts
            }));
        }
        await Promise.all(work);
    }

    function getAlwaysScopeKeys() {
        const keys = [];
        for (const [scopeKey, scopeMeta] of loadedScopes) {
            if (scopeMeta.mode === 'always') keys.push(scopeKey);
        }
        return keys;
    }

    function selectHandlersForTrigger(handlers, scopeKeys) {
        if (scopeKeys.size === 0) {
            return handlers.filter(h => h.__scopeMode === 'always');
        }
        return handlers.filter(h => h.__scopeMode === 'always' || (h.__scopeKey != null && scopeKeys.has(h.__scopeKey)));
    }

    async function trigger(triggerName, coreFn, eventInit = {}, opts = {}) {
        if (opts.scope && !Array.isArray(opts.scope) && !opts.scope.type) {
            const hasMappedScope = opts.scope.tenantId || opts.scope.eventId || opts.scope.templateId || opts.scope.pageId;
            if (hasMappedScope) {
                await loadScopeMap(opts.scope, opts.scopeMode || 'preload');
            }
        }

        const scopeKeys = normalizeTriggerScopeKeys(opts.scope);
        const selectedScopeKeys = new Set(getAlwaysScopeKeys());
        for (const scopeKey of scopeKeys) selectedScopeKeys.add(scopeKey);

        const beforeHandlers = selectHandlersForTrigger(before.get(triggerName) || [], selectedScopeKeys);
        const afterHandlers = selectHandlersForTrigger(after.get(triggerName) || [], selectedScopeKeys);

        const runtimeEventInit = (eventInit && typeof eventInit === 'object') ? eventInit : {};
        runtimeEventInit.type = triggerName;
        if (runtimeEventInit.canPreventDefault === undefined && typeof opts.canPreventDefault === 'boolean') {
            runtimeEventInit.canPreventDefault = opts.canPreventDefault;
        }
        try {
            return await runTriggerPipeline(beforeHandlers, afterHandlers, coreFn, runtimeEventInit);
        } catch (error) {
            console.error(`[scripts] Trigger failed: ${triggerName}`, {
                scope: opts.scope,
                selectedScopeKeys: [...selectedScopeKeys],
                error
            });
            throw error;
        }
    }

    function scoped(scope, opts = {}) {
        const baseScope = Array.isArray(scope) || (scope && scope.type && scope.id)
            ? scope
            : mergeScopeMaps({}, scope || {});
        const baseScopeMode = opts.scopeMode || 'preload';

        return {
            trigger: (triggerName, coreFn, eventInit = {}, triggerOpts = {}) => {
                const mergedScope = Array.isArray(baseScope) || (baseScope && baseScope.type && baseScope.id)
                    ? mergeTriggerScopes(baseScope, triggerOpts.scope)
                    : mergeScopeMaps(baseScope, triggerOpts.scope);
                const finalOpts = { ...triggerOpts };
                if (mergedScope && (!Array.isArray(mergedScope) ? Object.keys(mergedScope).length > 0 : mergedScope.length > 0)) {
                    finalOpts.scope = mergedScope;
                    finalOpts.scopeMode = triggerOpts.scopeMode || baseScopeMode;
                }
                return trigger(triggerName, coreFn, eventInit, finalOpts);
            },
            withScope: (extraScope, extraOpts = {}) => {
                const mergedScope = Array.isArray(baseScope) || (baseScope && baseScope.type && baseScope.id)
                    ? mergeTriggerScopes(baseScope, extraScope)
                    : mergeScopeMaps(baseScope, extraScope);
                return scoped(mergedScope, { scopeMode: extraOpts.scopeMode || baseScopeMode });
            },
            loadScope: (type, id, loadOpts = {}) => loadScope(type, id, loadOpts),
            unloadScope: (type, id) => unloadScope(type, id)
        };
    }

    function unloadScope(type, id) {
        assertScopeType(type);
        const idString = assertScopeId(id);
        const scopeKey = toScopeKey(type, idString);
        if (!loadedScopes.has(scopeKey)) return;

        for (const phaseMap of [before, after]) {
            for (const [triggerName, handlers] of phaseMap) {
                phaseMap.set(triggerName, handlers.filter(h => h.__scopeKey !== scopeKey));
            }
            pruneEmptyTriggerEntries(phaseMap);
        }

        loadedScopes.delete(scopeKey);
    }

    return {
        createScript: createScriptFactory,
        register(scriptId, scriptInstance, scopeMeta) {
            if (!scopeMeta?.scopeKey) throw new Error('[scripts] register requires explicit scopeMeta');
            registerWithScope(scopeMeta, scriptId, scriptInstance);
        },
        createScopedRegistrar,
        loadScope,
        loadScopes,
        trigger,
        scoped,
        unloadScope,
        _internals: {
            before,
            after,
            loadedScopes,
            pendingLoads,
            toScopeKey,
            normalizeTriggerScopeKeys,
            mergeTriggerScopes,
            mergeScopeMaps,
            getScopeKeys
        }
    };
}
